import { Helmet } from "react-helmet";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import LogoSeparator from "../components/separator";
import ProfessionGallery from "../components/ProfessionGallery";
import PartnerCarousel from "../components/Cover";
import TeachersCourse from "../components/Teacher-Courses";
// import GatCourse from "../components/GatCourse"; // section hidden
import TalentTrackSection from "../components/TextSection";
import ManagmentCourses from "../components/mangment-Courses";
import "./styles/particles.scss";
import "./styles/home-modern.css";
import PartnerSlider from "../components/PartnerSlider";
// import GATCertificates from "../components/GatCertificateCourses"; // section hidden
import ExamRegistrationCards from "../components/ExamRegistrationCards";
import OurCourses from "../components/OurCourses";

function HomePage() {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const el = document.getElementById(location.state.scrollTo);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, [location.state]);

  // Scroll-reveal (sections fade/slide in) + scroll-progress bar.
  useEffect(() => {
    const root = document.querySelector('.home-modern');
    if (!root) return;

    const cleanups = [];

    // ---- Reveal ----
    const targets = root.querySelectorAll(':scope > section, :scope > .container');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('tt-in'));
    } else {
      root.classList.add('tt-reveal-ready');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('tt-in');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );
      targets.forEach((el) => observer.observe(el));
      cleanups.push(() => observer.disconnect());
    }

    // ---- Scroll progress ----
    const bar = document.querySelector('.tt-scroll-progress-bar');
    if (bar) {
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const h = document.documentElement;
          const max = h.scrollHeight - h.clientHeight;
          const pct = max > 0 ? (h.scrollTop / max) * 100 : 0;
          bar.style.width = pct + '%';
          ticking = false;
        });
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(() => window.removeEventListener('scroll', onScroll));
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  // Dynamically update meta description and keywords in <head>
  useEffect(() => {
    // Description
    const descriptionContent =
      'Talent Track offers DHA assessment solutions for Dubai Health Authority certifications, alongside reliable tests for schools, universities, and career readiness. Enhance your admissions and certifications with Talent Track.';
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', descriptionContent);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = descriptionContent;
      document.head.appendChild(metaDescription);
    }
    // Keywords
    const keywordsContent =
      'DHA certification training, HR and PMP certification courses, professional development assessments';
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywordsContent);
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      metaKeywords.content = keywordsContent;
      document.head.appendChild(metaKeywords);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Talent Track | DHA Assessment Solutions and Certifications
        </title>
        <meta
          name="description"
          content="Talent Track offers DHA assessment solutions for Dubai Health Authority certifications, alongside reliable tests for schools, universities, and career readiness. Enhance your admissions and certifications with Talent Track."
        />

        <meta
          property="og:title"
          content="Talent Track - Your Path to Skill Development and Certifications"
        />
        <meta
          property="og:description"
          content="Talent Track offers tailored programs in various fields to help professionals and students advance their skills."
        />
        <meta property="og:url" content="https://talenttrackedu.com/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://talenttrackedu.com/logo192.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Talent Track - Career-Enhancing Programs and Certifications"
        />
        <meta
          name="twitter:description"
          content="Discover a wide array of Talent Track programs to enhance your skills and advance your career in various fields."
        />
        <meta
          name="twitter:image"
          content="https://talenttrackedu.com/logo192.png"
        />
        <link rel="canonical" href="https://talenttrackedu.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            name: "Talent Track",
            url: "https://talenttrackedu.com",
            description:
              "Talent Track offers professional courses, certifications, and skill development programs tailored to help individuals advance their careers.",
            logo: "https://talenttrackedu.com/logo192.png",
            faq: [
              {
                "@type": "Question",
                name: "What is Talent Track?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Talent Track is a platform designed to help students excel in their careers by offering professional certifications, skill-enhancement programs, and tailored career courses.",
                },
              },
              {
                "@type": "Question",
                name: "What programs are offered by Talent Track?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Talent Track offers programs like HR Diploma, Digital Marketing, GAT preparation, and management-focused courses designed to boost your skills.",
                },
              },
              {
                "@type": "Question",
                name: "How can I register for a Talent Track course?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "To register for a Talent Track course, visit the Courses page, select your desired program, and follow the registration process.",
                },
              },
              {
                "@type": "Question",
                name: "Does Talent Track provide certifications?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes, Talent Track provides certifications upon course completion to help students validate their skills and enhance their career opportunities.",
                },
              },
              {
                "@type": "Question",
                name: "How can I contact Talent Track support?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "You can reach Talent Track support by visiting the Contact Us page or emailing us at support@talenttrack.com.",
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="tt-scroll-progress" aria-hidden="true">
        <div className="tt-scroll-progress-bar"></div>
      </div>

      <div className="home-modern">
      <div className="tt-aurora" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div style={{ marginTop: '32px' }}></div>
      <PartnerCarousel />
      <div
        className="animation-wrapper"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: -1,
          contain: 'layout style paint'
        }}
      >
        <div
          className="particle particlee-2"
          style={{
            position: 'absolute',
            willChange: 'transform',
            transform: 'translate3d(0, 0, 0)',
            contain: 'layout style paint'
          }}
        ></div>
        <div
          className="particle particlee-4"
          style={{
            position: 'absolute',
            willChange: 'transform',
            transform: 'translate3d(0, 0, 0)',
            contain: 'layout style paint'
          }}
        ></div>
      </div>

      {/* <LogoSeparator /> */}
      {/* General Medical Exams Advertisement Section */}
      {/* <section style={{ background: '#e9f7ef', padding: '32px 0', margin: '32px 0 0 0', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontWeight: '700', color: '#007bff' }}>Book Your Medical Exam Today!</h2>
          <p style={{ fontSize: '18px', color: '#333', margin: '18px 0 28px 0' }}>Fast-track your healthcare career with Talent Track. Register for top medical exams in the GCC and get expert support every step of the way.</p>
          <BookNowButton text="Book Now" redirectPath="/Register" style={{ margin: '0 auto', display: 'block' }} size="md" />
        </div>
      </section> */}
      <LogoSeparator />
      <OurCourses />
      <LogoSeparator />

      <ExamRegistrationCards />
      <LogoSeparator />

      <ProfessionGallery />
      <LogoSeparator />

      <TalentTrackSection />
      <LogoSeparator />
      <ManagmentCourses />
      <LogoSeparator />
      {/* GAT certificates section hidden
      <GATCertificates />
      <LogoSeparator />
      */}
      <TeachersCourse />
      <LogoSeparator />
      {/* GAT course section hidden
      <GatCourse />
      <LogoSeparator />
      */}
      <PartnerSlider />
      </div>
    </>
  );
}

export default HomePage;
